import { observer } from "mobx-react-lite";
import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import { useSchemaEditor } from "..";
import { ArrayItemsSchema, SchemaParameterType } from "../types";

import Enums from "./Enums";
import TypeSelector from "./TypeSelector";
import { ISchemaParameter } from "./schema-parameter-store";

import SchemaParameter from ".";

import { Textarea } from "@/components/ui/textarea";

interface ArrayItemsEnumsProps {
  items: ArrayItemsSchema;
  onChangeItems: (value: ArrayItemsSchema) => void;
  isReadonly: boolean;
}

const ArrayItemsEnums = observer(({ items, onChangeItems, isReadonly }: ArrayItemsEnumsProps) => {
  const enumsRecord = useMemo(() => {
    if (!items.enum) {
      return undefined;
    }
    return items.enum.reduce(
      (acc, value) => {
        acc[uuidv4()] = value;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [items.enum]);

  const hasEnum = items.enum !== undefined && items.enum.length > 0;

  const toggleEnums = useCallback(() => {
    if (hasEnum) {
      const { enum: _removed, ...rest } = items;
      onChangeItems(rest as ArrayItemsSchema);
    } else {
      onChangeItems({
        ...items,
        enum: [""],
      });
    }
  }, [hasEnum, items, onChangeItems]);

  const addEnum = useCallback(() => {
    onChangeItems({
      ...items,
      enum: [...(items.enum || []), ""],
    });
  }, [items, onChangeItems]);

  const setEnum = useCallback(
    (key: string, value: string) => {
      if (!enumsRecord) {
        return;
      }
      const keys = Object.keys(enumsRecord);
      const index = keys.indexOf(key);
      if (index === -1) {
        return;
      }

      const newEnums = [...(items.enum || [])];
      newEnums[index] = value;
      onChangeItems({
        ...items,
        enum: newEnums,
      });
    },
    [enumsRecord, items, onChangeItems],
  );

  const deleteEnum = useCallback(
    (key: string) => {
      if (!enumsRecord) {
        return;
      }
      const keys = Object.keys(enumsRecord);
      const index = keys.indexOf(key);
      if (index === -1) {
        return;
      }

      const newEnums = [...(items.enum || [])];
      newEnums.splice(index, 1);

      if (newEnums.length === 0) {
        const { enum: _removed, ...rest } = items;
        onChangeItems(rest as ArrayItemsSchema);
      } else {
        onChangeItems({
          ...items,
          enum: newEnums,
        });
      }
    },
    [enumsRecord, items, onChangeItems],
  );

  const handleNoOp = useCallback(() => {
    // No-op handler for disabled features in array items enums
  }, []);

  return (
    <Enums
      enums={enumsRecord}
      enumName={undefined}
      includeEnum={hasEnum}
      isEnumVariable={false}
      onNameChange={handleNoOp}
      toggleEnumMode={handleNoOp}
      addEnum={() => {
        if (!isReadonly) {
          addEnum();
        }
      }}
      setEnum={(k, v) => {
        if (!isReadonly) {
          setEnum(k, v);
        }
      }}
      toggleEnums={() => {
        if (!isReadonly) {
          toggleEnums();
        }
      }}
      deleteEnum={k => {
        if (!isReadonly) {
          deleteEnum(k);
        }
      }}
    />
  );
});

// Component for editing simple array items (array of primitives)
interface SimpleArrayItemsEditorProps {
  items: ArrayItemsSchema;
  onChangeItems: (value: ArrayItemsSchema) => void;
}

const SimpleArrayItemsEditor = observer(({ items, onChangeItems }: SimpleArrayItemsEditorProps) => {
  const editor = useSchemaEditor();
  const { isReadonly } = editor;

  const enumsAvailable = items.type === "string" || items.type === "number";

  return (
    <div className="mb-4 flex flex-col gap-y-2 pl-6">
      <TypeSelector
        value={items.type as SchemaParameterType}
        onChange={value => {
          if (isReadonly) {
            return;
          }
          const newItems: ArrayItemsSchema = {
            type: value,
            description: items.description,
          };
          if (items.additionalProperties !== undefined) {
            newItems.additionalProperties = items.additionalProperties;
          }
          // Preserve enum only when switching between enum-compatible types (string/number)
          const newTypeSupportsEnum = value === "string" || value === "number";
          if (items.enum && newTypeSupportsEnum) {
            newItems.enum = items.enum;
          }
          onChangeItems(newItems);
        }}
        disabled={isReadonly}
      />
      <Textarea
        placeholder="Item Description"
        value={items.description}
        onChange={e => {
          if (isReadonly) {
            return;
          }
          onChangeItems({
            ...items,
            description: e.target.value,
          });
        }}
        readOnly={isReadonly}
      />
      {enumsAvailable && (
        <ArrayItemsEnums items={items} onChangeItems={onChangeItems} isReadonly={isReadonly} />
      )}
    </div>
  );
});

// Component for rendering nested children (composition arrays/objects)
interface ChildrenRendererProps {
  parameters: ISchemaParameter[];
}

const ChildrenRenderer = observer(({ parameters }: ChildrenRendererProps) => (
  <div className="flex flex-col">
    {parameters.map((param, index) => (
      <SchemaParameter
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        parameter={param}
      />
    ))}
  </div>
));

// Main content wrapper component
interface ParameterContentWrapperProps {
  parameter: ISchemaParameter;
  children?: React.ReactNode;
  before?: React.ReactNode;
}

const ParameterContentWrapper = observer(
  ({ parameter, children, before }: ParameterContentWrapperProps) => {
    const editor = useSchemaEditor();
    const { isReadonly } = editor;
    const hasNestedContent = parameter.children.length > 0 || parameter.items !== undefined;

    return (
      <div
        className={`mb-3 flex flex-col gap-y-2 ${
          hasNestedContent
            ? "relative before:absolute before:left-2 before:top-0 before:h-full before:w-[1px] before:bg-gray-100 before:content-[''] after:absolute after:bottom-0 after:left-2 after:h-[1px] after:w-full after:bg-gray-100 after:content-[''] hover:before:bg-gray-300 hover:after:bg-gray-300"
            : ""
        }`}
      >
        <div className="flex justify-center">{before}</div>

        {/* Description field - always shown */}
        {hasNestedContent ? (
          <div className="mb-2 pl-6">
            <Textarea
              placeholder="Description"
              value={parameter.description}
              onChange={e => {
                if (!isReadonly) {
                  parameter.setDescription(e.target.value);
                }
              }}
              readOnly={isReadonly}
            />
          </div>
        ) : (
          <Textarea
            placeholder="Description"
            value={parameter.description}
            onChange={e => {
              if (!isReadonly) {
                parameter.setDescription(e.target.value);
              }
            }}
            readOnly={isReadonly}
          />
        )}

        {/* Simple array items editor */}
        {parameter.items && (
          <SimpleArrayItemsEditor
            items={parameter.items}
            onChangeItems={value => {
              if (!isReadonly) {
                parameter.setItems(value);
              }
            }}
          />
        )}

        {/* Nested children renderer */}
        {parameter.children.length > 0 && <ChildrenRenderer parameters={parameter.children} />}

        {children}
      </div>
    );
  },
);

export default ParameterContentWrapper;
